// Pending reads are scoped to one selected world. Settled results are never
// retained across turns or character selections.
const pending = new Map();
export const acquirePlayBootstrap = (key, start) => {
  let entry = pending.get(key);
  if (!entry) {
    entry = { users: 0 };
    entry.promise = new Promise((resolve, reject) => {
      entry.reject = reject;
      entry.request = start((error, result) => error || result?.error
        ? reject(Object.assign(new Error(result?.error?.message || 'Failed to load the game.'), { status: error })) : resolve(result));
    }).finally(() => { if (pending.get(key) === entry) pending.delete(key); });
    pending.set(key, entry);
  }
  entry.users++;
  let released = false;
  return { promise: entry.promise, release: () => {
    if (released) return;
    released = true;
    entry.users--;
    // React may immediately reattach the same effect during development.
    queueMicrotask(() => {
      if (entry.users || pending.get(key) !== entry) return;
      pending.delete(key);
      entry.request?.abort?.();
      entry.reject(Object.assign(new Error('Game loading cancelled.'), { name: 'AbortError' }));
    });
  } };
};

export const expandBootstrapPlaces = bootstrap => {
  const places = (bootstrap.places || []).map(place => ({ ...place, shape: place.shape || place.shapes?.[0] }));
  const expand = place => place && ({ ...place,
    composedOfChildren: places.filter(child => child.parentIds?.includes(place.idEntity)),
  });
  const byId = new Map(places.map(place => [place.idEntity, expand(place)]));
  const place = byId.get(bootstrap.currentPlaceId);
  return { places: [...byId.values()], placeChain: bootstrap.placeChain.map(id => byId.get(id)),
    currentPlaceContext: { place, subPlaces: place?.composedOfChildren || [], beingsInLocation: [], services: [] } };
};

export const createBackgroundReads = (limit = 2) => {
  let running = 0, disposed = false;
  const queue = [], values = new Map();
  const complete = () => { running--; pump(); };
  const pump = () => {
    while (!disposed && running < limit && queue.length) {
      const task = queue.shift(); running++;
      Promise.resolve().then(task.read).then(task.resolve, task.reject).finally(complete);
    }
  };
  const load = (key, read) => {
    if (disposed) return Promise.reject(Object.assign(new Error('Scene loading cancelled.'), { name: 'AbortError' }));
    if (!values.has(key)) values.set(key, new Promise((resolve, reject) => { queue.push({ read, resolve, reject }); pump(); }));
    return values.get(key);
  };
  load.dispose = () => {
    disposed = true;
    queue.splice(0).forEach(task => task.reject(Object.assign(new Error('Scene loading cancelled.'), { name: 'AbortError' })));
  };
  load.isDisposed = () => disposed;
  return load;
};
